import { useAuth, useUser } from '../../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuAction } from '@ordr-as/umami-react/context-menu';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { Item } from '@ordr-as/umami-react/form';
import { HamburgerMenu, Header, TabMenu } from '@ordr-as/umami-react/header';
import FormattedMessage from '../FormattedMessage';
import { useQueryDigitalBillFeatureFlag, useQuickerLoginEnabled } from '../../hooks/useFeatureFlagApi';
import LocationPicker from './LocationPicker';
import { useIosAppVersion } from '../../hooks/useIosAppVersion';
import { useF1AppVersion } from '../../hooks/useF1AppVersion';
import { useQueryLocation, useQueryLocationInfo } from '../../hooks/useLocationApi';
import ChangeUser from './ChangeUser';
import LogOut from './LogOut';
import TermsAndConditions from './TermsAndConditions';
import AdminRedirect from './AdminRedirect';
import PinScreenSettings from './PinScreenSettings';
import { createTerminalModeUrl } from '../../utils/linkBuilder';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useTerminalModeEnabled } from '../../hooks/useTerminalModeEnabled';
import AppReleaseVersion from './AppReleaseVersion';
import { isF1Device } from '../../utils/isF1Device';
import { useGetStations } from '@ordr-as/ordr-service-types/epos';
import { isS1F2Device } from '../../utils/isS1F2Device';
import { useKdsNavEnabled } from '../../hooks/useKdsNavEnabled';
import KdsNavToggle from './KdsNavToggle';

declare global {
  interface Window {
    Android?: {
      navigateToTerminal?: () => void;
      logout?: () => void;
      changeLocation?: () => void;
      getConnectionState?: () => boolean;
    };
  }
}

const nonLocationRoutes = new Set(['locations', 'softpay']); // Dette må vi få til bedre

const AppHeader = () => {
  const location = useLocation();
  const iosAppVersion = useIosAppVersion();
  const f1AppVersion = useF1AppVersion();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { logout, changeUser } = useAuth();
  const { email } = useUser() || {};

  const [showSwitchToTerminalModeDialog, setShowSwitchToTerminalModeDialog] = useState<boolean>(false);

  const currentLocationId = location.pathname.split('/')[1]!;
  const isNotALocation = nonLocationRoutes.has(currentLocationId);
  const locationId = isNotALocation ? undefined : currentLocationId;

  const { numericId } = useQueryLocation(locationId);
  const { data: favritLocation } = useQueryLocationInfo(numericId);

  const { data: digitalBillFeatureFlag } = useQueryDigitalBillFeatureFlag(favritLocation?.id);

  const { data: quickerLoginEnabled, isPending: isQuickerLoginPending } = useQuickerLoginEnabled(favritLocation?.id);

  const { data: stations } = useGetStations(favritLocation?.id ?? 0, {
    query: { enabled: favritLocation?.id !== undefined },
  });
  const hasStations = (stations?.length ?? 0) > 0;

  const { enabled: kdsNavEnabled, toggle: toggleKdsNav } = useKdsNavEnabled();

  const isTerminalModeEnabled = useTerminalModeEnabled();
  const isF1 = isF1Device();
  const isS1F2 = isS1F2Device();
  const canSignalAndroidTerminal = Boolean(window.Android?.navigateToTerminal);
  const canChangeLocation = Boolean(window.Android?.changeLocation);

  const appVersion = iosAppVersion ?? f1AppVersion;
  const showLogoutButton = !(appVersion && !quickerLoginEnabled?.enabled);

  const secondSegment = location.pathname.split('/')[2];
  const selectedTab = secondSegment === 'open-bill' ? 'open-bill' : secondSegment === 'kds' ? 'kds' : 'menu';

  const currentLocation = window.location.href;

  const handleTerminalModeClick = () => {
    setShowSwitchToTerminalModeDialog(true);
  };

  const handleTerminalModeConfirm = (shouldNavigate: boolean) => {
    setShowSwitchToTerminalModeDialog(false);

    if (shouldNavigate) {
      navigate(createTerminalModeUrl(currentLocationId));
    }
  };

  if (currentLocation.includes('/cart')) {
    return null;
  }

  const canInitiateDigitalBill = Boolean(digitalBillFeatureFlag?.waiterCanInitiate);
  // Menu is the catch-all destination, so it should also be reachable when only
  // KDS is enabled — but Open Bills stays gated on the digital-bill flag.
  const showMenu = canInitiateDigitalBill || hasStations;

  const tabItems = [];
  if (showMenu) {
    tabItems.push(
      <Item key="menu" textValue="menu">
        <Link to={`/${currentLocationId}`} className="text-brand-secondary">
          <FormattedMessage id="generic.menu" />
        </Link>
      </Item>
    );
  }
  if (canInitiateDigitalBill) {
    tabItems.push(
      <Item key="open-bill" textValue="open-bill">
        <Link
          to={`/${currentLocationId}/open-bill`}
          data-cy="open-bill"
          className="whitespace-nowrap text-brand-secondary"
        >
          <FormattedMessage id="generic.OpenBills" />
        </Link>
      </Item>
    );
  }
  if (hasStations && kdsNavEnabled) {
    tabItems.push(
      <Item key="kds" textValue="kds">
        <Link to={`/${currentLocationId}/kds/expo`} data-cy="kds" className="whitespace-nowrap text-brand-secondary">
          <FormattedMessage id="generic.kds" />
        </Link>
      </Item>
    );
  }

  return (
    <>
      <Header headerClass="flex justify-between" hideBackButton backAction={() => {}}>
        {tabItems.length > 0 ? (
          <TabMenu selectedKeys={[selectedTab]} selectedBorderColor="border-brand-secondary" type="compact">
            {tabItems}
          </TabMenu>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-1 ml-2">
          <div className="flex flex-col text-right text-xs max-w-[33vw]">
            {favritLocation?.name && (
              <span className="text-brand-secondary font-semibold truncate">{favritLocation?.name}</span>
            )}
            <span className="text-grey-dark truncate">{email}</span>
          </div>

          <HamburgerMenu>
            {favritLocation && (isQuickerLoginPending || quickerLoginEnabled?.enabled) && (
              <div className={isQuickerLoginPending ? 'invisible' : ''}>
                <ChangeUser email={email} onClick={() => changeUser(favritLocation.id)} />
              </div>
            )}
            {!iosAppVersion && !isF1 && !canChangeLocation && <LocationPicker />}
            {isF1 && canChangeLocation && (
              <MenuAction className="border-b border-grey-lightest" onClick={() => window.Android?.changeLocation?.()}>
                <span className="text-standard !text-brand-black">
                  {formatMessage({ id: 'header.changeLocation' })}
                </span>
              </MenuAction>
            )}
            {favritLocation && (
              <Link
                className="umami-context-menu-item border-b border-grey-lightest !py-3 !text-brand-black"
                to={`/${currentLocationId}/settings`}
                state={{ backgroundLocation: location }}
              >
                <FormattedMessage id="header.paymentMethods" />
              </Link>
            )}
            {favritLocation && (
              <Link
                className="umami-context-menu-item border-b border-grey-lightest !py-3 !text-brand-black"
                to={`/${currentLocationId}/recent-payments`}
                state={{ backgroundLocation: location }}
              >
                <FormattedMessage id="header.payments" />
              </Link>
            )}
            {favritLocation && (isQuickerLoginPending || quickerLoginEnabled?.enabled) && (
              <div className={isQuickerLoginPending ? 'invisible' : ''}>
                <PinScreenSettings />
              </div>
            )}
            {isTerminalModeEnabled && (
              <MenuAction className="border-b border-grey-lightest" onClick={handleTerminalModeClick}>
                <span className="text-standard !text-brand-black">{formatMessage({ id: 'header.terminalMode' })}</span>
              </MenuAction>
            )}
            {canSignalAndroidTerminal && (
              <MenuAction
                className="border-b border-grey-lightest"
                onClick={() => window.Android?.navigateToTerminal?.()}
              >
                <span className="text-standard !text-brand-black">Open terminal mode</span>
              </MenuAction>
            )}
            {!iosAppVersion && !isF1 && !isS1F2 && <AdminRedirect locationId={favritLocation?.id} />}
            {!isF1 && !isS1F2 && <TermsAndConditions />}
            {(showLogoutButton || (isQuickerLoginPending && Boolean(appVersion))) && (
              <div className={isQuickerLoginPending && !showLogoutButton ? 'invisible' : ''}>
                <LogOut
                  onClick={() => {
                    if (isF1) {
                      window.Android?.logout?.();
                      return;
                    }
                    logout();
                  }}
                />
              </div>
            )}
            {hasStations && <KdsNavToggle enabled={kdsNavEnabled} onToggle={toggleKdsNav} />}
            <AppReleaseVersion isF1={isF1} />
          </HamburgerMenu>
        </div>
      </Header>

      {showSwitchToTerminalModeDialog && (
        <Dialog
          title="Go to terminal mode?"
          onClose={() => handleTerminalModeConfirm(false)}
          onAction={() => handleTerminalModeConfirm(true)}
          actionLabel="Go to terminal mode"
        >
          <p>The terminal will display a static screen sutiable for facing the guests.</p>
          <p className="mt-4">You can switch back to ePOS mode in the hamburger menu on the terminal anytime.</p>
        </Dialog>
      )}
    </>
  );
};
export default AppHeader;
